<section class="flex min-w-0 flex-col gap-3 p-3">
  @if ($loading()) {
    <tui-loader class="min-h-48" [loading]="true"></tui-loader>
  } @else {
    @if ($missingRequestsCount()) {
      <div
        class="rounded-[var(--tui-radius-m)] bg-[var(--tui-status-negative-pale)] p-3 text-[var(--tui-status-negative)]"
      >
        {{
          'group_actions.files_upload.missing_requests'
            | translate: { count: $missingRequestsCount() }
        }}
      </div>
    }

    <div class="x-card x-card_size_s bg-[var(--tui-background-base)]">
      <h3 class="mb-2 text-base font-bold">
        {{ 'group_actions.files_upload.category_title' | translate }}
      </h3>
      <tui-textfield
        tuiChevron
        [stringify]="categoryStringify"
        [tuiTextfieldCleaner]="true"
        tuiTextfieldSize="m"
        tuiDropdownLimitWidth="fixed"
      >
        <label tuiLabel>
          {{ 'group_actions.files_upload.category_placeholder' | translate }}
        </label>
        <input
          tuiComboBox
          [formControl]="categoryControl"
          (input)="$categorySearch.set($any($event.target).value ?? '')"
        />
        <tui-data-list *tuiDropdown>
          @if ($ownerCategories().length) {
            <tui-opt-group
              [label]="
                'group_actions.files_upload.owner_categories' | translate
              "
            >
              @for (category of $ownerCategories(); track category.id) {
                <button tuiOption [value]="category">
                  {{ category.name }}
                </button>
              }
            </tui-opt-group>
          }
          @if ($contractorCategories().length) {
            <tui-opt-group
              [label]="
                'group_actions.files_upload.contractor_categories' | translate
              "
            >
              @for (category of $contractorCategories(); track category.id) {
                <button tuiOption [value]="category">
                  {{ category.name }}
                </button>
              }
            </tui-opt-group>
          }
        </tui-data-list>
      </tui-textfield>
    </div>

    <div class="x-card x-card_size_s bg-[var(--tui-background-base)]">
      <div
        class="flex items-center justify-between gap-3 max-sm:flex-col max-sm:items-stretch"
      >
        <div>
          <h3 class="mb-2 text-base font-bold">
            {{ 'group_actions.files_upload.files_title' | translate }}
          </h3>
          <p class="m-0 text-xs text-[var(--tui-text-secondary)]">
            {{ 'group_actions.files_upload.files_hint' | translate }}
          </p>
        </div>
        @if ($files().length) {
          <button
            tuiButton
            type="button"
            appearance="secondary"
            size="s"
            [disabled]="$uploading()"
            (click)="distributeFiles()"
          >
            {{ 'group_actions.files_upload.distribute' | translate }}
          </button>
        }
      </div>

      <input
        #fileInput
        hidden
        type="file"
        multiple
        (change)="onFileInput(fileInput)"
      />
      <button
        class="mt-3 flex min-h-28 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-[var(--tui-radius-m)] border-2 border-dashed border-[var(--tui-border-normal)] bg-[var(--tui-background-base-alt)] text-[var(--tui-text-secondary)] aria-[dragging=true]:border-[var(--tui-background-accent-1)] aria-[dragging=true]:bg-[var(--tui-background-accent-1-bg)]"
        type="button"
        [attr.aria-dragging]="$dragging()"
        [disabled]="$uploading()"
        (click)="fileInput.click()"
        (dragenter)="$dragging.set(true)"
        (dragover)="$event.preventDefault()"
        (dragleave)="$dragging.set(false)"
        (drop)="onDrop($event)"
      >
        <strong>{{
          'group_actions.files_upload.drop_title' | translate
        }}</strong>
        <span>{{ 'group_actions.files_upload.drop_hint' | translate }}</span>
      </button>
    </div>

    @if ($files().length) {
      <div class="grid gap-3">
        @for (stagedFile of $files(); track stagedFile.id) {
          <div class="x-card x-card_size_s bg-[var(--tui-background-base)]">
            <div class="flex items-center justify-between gap-3">
              <div class="flex min-w-0 items-center gap-3">
                @if (stagedFile.previewUrl) {
                  <button
                    type="button"
                    class="h-12 w-12 shrink-0 overflow-hidden rounded-[var(--tui-radius-s)] border-0 bg-transparent p-0"
                    [disabled]="$uploading()"
                    (click)="showPreview(stagedFile.previewUrl)"
                  >
                    <img
                      class="h-full w-full object-cover"
                      [src]="stagedFile.previewUrl"
                      [alt]="
                        'group_actions.files_upload.preview_image_alt'
                          | translate
                      "
                    />
                  </button>
                }
                <div class="min-w-0">
                  <strong class="block break-all">{{
                    stagedFile.file.name
                  }}</strong>
                  <span
                    class="block text-xs text-[var(--tui-text-secondary)]"
                    >{{ formatFileSize(stagedFile.file.size) }}</span
                  >
                </div>
              </div>
              <button
                tuiIconButton
                type="button"
                appearance="flat"
                iconStart="@tui.trash"
                size="s"
                [title]="'group_actions.files_upload.remove_file' | translate"
                [disabled]="$uploading()"
                (click)="removeFile(stagedFile.id)"
              ></button>
            </div>

            <div
              class="mt-3 flex min-w-0 items-center gap-3 max-sm:flex-col max-sm:items-stretch"
            >
              <tui-textfield
                multi
                tuiChevron
                class="min-w-0 flex-1"
                [stringify]="requestStringify"
                tuiTextfieldSize="m"
                tuiDropdownLimitWidth="fixed"
              >
                <label tuiLabel>
                  {{
                    'group_actions.files_upload.requests_placeholder'
                      | translate
                  }}
                </label>
                <input
                  tuiInputChip
                  [formControl]="targetControl(stagedFile.id)"
                  (input)="
                    setRequestSearch(stagedFile.id, $any($event.target).value)
                  "
                />
                <tui-input-chip *tuiItem />
                <tui-data-list tuiMultiSelectGroup *tuiDropdown>
                  @for (
                    request of filteredRequests(stagedFile.id);
                    track request.id
                  ) {
                    <button tuiOption [value]="request">
                      {{ requestStringify(request) }}
                    </button>
                  }
                </tui-data-list>
              </tui-textfield>
              <div
                class="flex flex-wrap items-center gap-3 max-sm:flex-col max-sm:items-stretch"
              >
                <button
                  tuiButton
                  type="button"
                  appearance="secondary"
                  size="s"
                  [disabled]="$uploading()"
                  (click)="selectAllRequests(stagedFile.id)"
                >
                  {{ 'group_actions.files_upload.select_all' | translate }}
                </button>
                <button
                  tuiButton
                  type="button"
                  appearance="secondary"
                  size="s"
                  [disabled]="$uploading()"
                  (click)="clearAllRequests(stagedFile.id)"
                >
                  {{ 'group_actions.files_upload.clear_all' | translate }}
                </button>
              </div>
            </div>

            @switch (stagedFile.distribution) {
              @case ('matched') {
                <p class="mb-0 mt-2 text-xs text-[var(--tui-status-positive)]">
                  {{
                    'group_actions.files_upload.distribution_matched'
                      | translate
                  }}
                </p>
              }
              @case ('none') {
                <p class="mb-0 mt-2 text-xs text-[var(--tui-text-secondary)]">
                  {{
                    'group_actions.files_upload.distribution_none' | translate
                  }}
                </p>
              }
              @case ('ambiguous') {
                <p class="mb-0 mt-2 text-xs text-[var(--tui-status-negative)]">
                  {{
                    'group_actions.files_upload.distribution_ambiguous'
                      | translate
                  }}
                </p>
              }
            }

            @if ($results().length) {
              <div class="mt-3 grid gap-3">
                @for (
                  request of targetControl(stagedFile.id).value;
                  track request.id
                ) {
                  @let result = resultFor(stagedFile.id, request.id);
                  @if (result) {
                    <span
                      [class]="
                        result.status === 'success'
                          ? 'text-xs text-[var(--tui-status-positive)]'
                          : 'text-xs text-[var(--tui-status-negative)]'
                      "
                    >
                      {{ requestStringify(request) }}:
                      {{
                        (result.status === 'success'
                          ? 'group_actions.files_upload.uploaded'
                          : 'group_actions.files_upload.upload_error'
                        ) | translate
                      }}
                    </span>
                  }
                }
              </div>
            }
          </div>
        }
      </div>
    }

    <footer
      class="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded-[var(--tui-radius-m)] bg-[var(--tui-background-base)] p-3 max-sm:flex-col max-sm:items-stretch"
    >
      @if ($uploading()) {
        <div class="flex min-w-48 flex-1 items-center gap-3">
          <progress
            class="flex-1"
            tuiProgressBar
            [max]="uploadManager.$total() || 1"
            [value]="uploadManager.$completed()"
          ></progress>
          <span
            >{{ uploadManager.$completed() }} /
            {{ uploadManager.$total() }}</span
          >
        </div>
      }
      @if (hasFailures()) {
        <button
          tuiButton
          type="button"
          appearance="secondary"
          size="m"
          [disabled]="$uploading()"
          (click)="retryFailed()"
        >
          {{ 'group_actions.files_upload.retry_failed' | translate }}
        </button>
      }
      <button
        tuiButton
        type="button"
        appearance="primary"
        size="m"
        [disabled]="!canUpload()"
        (click)="upload()"
      >
        {{ 'group_actions.files_upload.upload' | translate }}
      </button>
    </footer>
  }
</section>

<ng-template #imagePreview let-previewObserver>
  <tui-preview>
    <button
      iconStart="@tui.x"
      [title]="'group_actions.files_upload.close_preview' | translate"
      tuiIconButton
      tuiPreviewAction
      type="button"
      (click)="previewObserver.complete()"
    ></button>
    @if ($previewUrl(); as previewUrl) {
      <img
        [src]="previewUrl"
        [alt]="'group_actions.files_upload.preview_image_alt' | translate"
      />
    }
  </tui-preview>
</ng-template>
